/**
 * The app's appearance: the house theme's Light (the default, the house's
 * since shell 1.4.0) or Dark (`docs/decisions/0054`, `0057`). The board look owns the game canvas, and follows
 * this too: a dark board on Dark, a light one on Light (`docs/decisions/0056`);
 * everything else (every screen, menu, sheet and button) is the shell's house
 * look, switched here.
 *
 * Two halves, both the shell's (`packages/shell/INTEGRATION.md` 4.3):
 *
 * - **before first paint**, the shell's `appearance-boot.js` (served at
 *   `/appearance-boot.js` by `vite.config.ts`, loaded blocking in
 *   `index.html` with `data-key="interlock:appearance"` and
 *   `data-default="light"`) stamps `data-appearance="light"` unless
 *   {@link APPEARANCE_STORAGE_KEY} holds `dark`;
 * - **at runtime**, `applyAppearance` stamps (or removes) the attribute and
 *   points `<meta name="theme-color">` at the ground the theme now paints.
 *
 * The setting itself stays where every other setting lives (the settings
 * blob, synced with an account); {@link showAppearance} mirrors it into the
 * boot script's own key, so the next load paints the right ground first.
 */
import { applyAppearance, storeAppearance } from '@artibens/app-shell';

import type { Appearance } from '../state/settings-io.js';

/** The key the shell's boot script reads (`index.html`'s `data-key`). */
export const APPEARANCE_STORAGE_KEY = 'interlock:appearance';

/**
 * Shows `appearance` and remembers it for the boot script. Also recolours
 * `<meta name="theme-color">` to the page's ground: called on start-up, on
 * every change of the setting, and when a game hands the browser chrome back
 * to the menus.
 */
export function showAppearance(appearance: Appearance): void {
  storeAppearance(APPEARANCE_STORAGE_KEY, appearance);
  applyAppearance(appearance);
}
